import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { GraduationCap, Eye, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  published: "bg-green-100 text-green-700",
  draft: "bg-amber-100 text-amber-700",
  archived: "bg-black/10 text-black/60",
};

export default function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => adminClient().get("/admin/courses").then(({ data }) => setCourses(data.courses || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const setStatus = async (slug, status) => {
    try {
      await adminClient().put(`/admin/courses/${slug}/status`, { status });
      setCourses((cs) => cs.map((c) => (c.slug === slug ? { ...c, status } : c)));
      toast.success(`Course ${status}.`);
    } catch { toast.error("Could not update course status."); }
  };

  const counts = courses.reduce((a, c) => { a[c.status] = (a[c.status] || 0) + 1; return a; }, {});

  return (
    <div data-testid="admin-courses">
      <div className="flex items-center gap-2 mb-1"><GraduationCap className="w-5 h-5 text-taven-gold" /><h1 className="text-2xl font-black font-display">TAVEN Learning Center</h1></div>
      <p className="text-sm text-muted-foreground mb-6">Publish, draft, or archive courses. Only <strong>published</strong> courses appear publicly. Draft/archived stay hidden until they meet TAVEN quality standards.</p>

      <div className="flex flex-wrap gap-3 mb-6">
        {["published", "draft", "archived"].map((s) => (
          <div key={s} className="border border-black/10 bg-white px-4 py-3 min-w-[120px]">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{s}</p>
            <p className="text-2xl font-black font-display">{counts[s] || 0}</p>
          </div>
        ))}
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <div className="border border-black/10 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-taven-gray/60 text-left"><tr>
              <th className="px-4 py-3 font-bold">Course</th>
              <th className="px-4 py-3 font-bold">Category</th>
              <th className="px-4 py-3 font-bold">Type</th>
              <th className="px-4 py-3 font-bold">Status</th>
              <th className="px-4 py-3 font-bold text-right">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-black/5">
              {courses.map((c) => (
                <tr key={c.slug} data-testid={`admin-course-${c.slug}`}>
                  <td className="px-4 py-3 font-semibold">
                    <div className="flex items-center gap-2">{c.flagship && <Sparkles className="w-3.5 h-3.5 text-taven-gold" />}{c.title}</div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{c.category}</td>
                  <td className="px-4 py-3">{c.rich ? <span className="text-xs font-bold text-taven-gold">Rich</span> : <span className="text-xs text-muted-foreground">Template</span>}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${STATUS_STYLE[c.status]}`}>{c.status}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      <a href={`/education/${c.slug}`} target="_blank" rel="noreferrer" className="text-xs font-bold text-black/60 hover:text-taven-gold flex items-center gap-1" data-testid={`preview-${c.slug}`}><Eye className="w-3.5 h-3.5" /> Preview</a>
                      {c.status !== "published" && <button onClick={() => setStatus(c.slug, "published")} data-testid={`publish-${c.slug}`} className="text-xs font-bold uppercase tracking-wider bg-green-600 text-white px-2.5 py-1.5 hover:bg-green-700">Publish</button>}
                      {c.status !== "draft" && <button onClick={() => setStatus(c.slug, "draft")} data-testid={`draft-${c.slug}`} className="text-xs font-bold uppercase tracking-wider bg-amber-500 text-white px-2.5 py-1.5 hover:bg-amber-600">Draft</button>}
                      {c.status !== "archived" && <button onClick={() => setStatus(c.slug, "archived")} data-testid={`archive-${c.slug}`} className="text-xs font-bold uppercase tracking-wider bg-black/70 text-white px-2.5 py-1.5 hover:bg-black">Archive</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
